import type {
    AppliedHeuristicLogMessage,
    AsyncStructureIterator,
    ComponentStageData,
    ConversionCompRef,
    ConversionDal,
    CreateComponentStageArgs,
    ConversionAlgoConfig,
    MobileAlgoContext,
    Stage,
    StageHandler,
    StructureTypeToEntryType,
    StageRuntimeStore
} from '../types'
import {pointerUtils, type SnapshotDal} from '@wix/document-manager-core'
import {NaryTreeTraversalOrder} from '../enums'
import {ReportableError} from '@wix/document-manager-utils'
import {createStructureIterators} from '../conversionDal/utils/structureIterators'
import {ConversionDalNamespaces, ConversionDalStructureNamespaces} from '../conversionDal/constants'

export const createComponentStage = (
    {
        traversalOrder = NaryTreeTraversalOrder.PreOrder,
        structuralType = ConversionDalNamespaces.structure
    }: CreateComponentStageArgs = {
        traversalOrder: NaryTreeTraversalOrder.PreOrder,
        structuralType: ConversionDalNamespaces.structure
    }
): Stage<ComponentStageData> => {
    if (!(traversalOrder in NaryTreeTraversalOrder)) {
        throw new ReportableError({
            errorType: 'InvalidConversionStructureTreeTraversalOrder',
            message: `traversalOrder should be one of the following: ${Object.values(NaryTreeTraversalOrder)}`
        })
    }

    if (!ConversionDalStructureNamespaces.includes(structuralType)) {
        throw new ReportableError({
            errorType: 'InvalidConversionStructuralType',
            message: `structuralType should be one of the following: ${ConversionDalStructureNamespaces}`
        })
    }

    const handlers: StageHandler<ComponentStageData>[] = []

    const _run = async (
        ctx: MobileAlgoContext,
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        iterator: AsyncStructureIterator<StructureTypeToEntryType[typeof structuralType]>,
        pageId: string,
        config: ConversionAlgoConfig,
        stageRuntimeStore: StageRuntimeStore,
        logger?: AppliedHeuristicLogMessage[]
    ) => {
        await iterator(async (component: StructureTypeToEntryType[typeof structuralType]) => {
            const componentPointer: ConversionCompRef = pointerUtils.getPointer(
                component.id,
                structuralType
            ) as ConversionCompRef

            const handlersResults = await Promise.all(
                handlers.map(handler =>
                    handler({
                        globalContext: ctx,
                        conversionDal,
                        initialSnapshot,
                        componentPointer,
                        pageId,
                        config,
                        stageRuntimeStore
                    })
                )
            )

            if (logger) {
                for (const results of handlersResults) {
                    if (results) {
                        logger.push({
                            componentId: componentPointer.id,
                            appliedHeuristics: results.appliedHeuristics.filter(Boolean),
                            timestamp: new Date().toISOString()
                        })
                    }
                }
            }
        })
    }

    const run = async (
        ctx: MobileAlgoContext,
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        pageId: string,
        config: ConversionAlgoConfig,
        stageRuntimeStore: StageRuntimeStore,
        logger?: AppliedHeuristicLogMessage[]
    ) => {
        if (handlers.length === 0) {
            return
        }

        const rootPointerInCurrentStructure = pointerUtils.getPointer(config.rootPointer.id, structuralType)

        if (!conversionDal.get(rootPointerInCurrentStructure)) {
            return
        }

        const structureIterators = createStructureIterators(conversionDal, rootPointerInCurrentStructure)

        if (traversalOrder === NaryTreeTraversalOrder.PostOrder) {
            await _run(
                ctx,
                conversionDal,
                initialSnapshot,
                structureIterators.forEachComponentPostOrderAsync,
                pageId,
                config,
                stageRuntimeStore,
                logger
            )
        } else if (traversalOrder === NaryTreeTraversalOrder.PreOrder) {
            await _run(
                ctx,
                conversionDal,
                initialSnapshot,
                structureIterators.forEachComponentAsync,
                pageId,
                config,
                stageRuntimeStore,
                logger
            )
        }
    }

    return {
        register: (handler: StageHandler<ComponentStageData>) => {
            handlers.push(handler)
        },
        run
    }
}
